/* Zevari MCP vault — token + LinkedIn action probe. */
import GenericVault from "./GenericVault";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plug, CheckCircle2, AlertTriangle, Send, Loader2, Database, ExternalLink, FlaskConical, ShieldCheck } from "lucide-react";

/* ----------------------------------------------------------------- */
/*  Zevari — MCP token vault + live read-only MCP probe panel.        */
/*  Every LinkedIn WRITE stays behind Zevari's human approval gate;   */
/*  the probe panel only runs READ tools (context/safety/stats).      */
/* ----------------------------------------------------------------- */
function ZevariVault({ provider, cfg, onSaved }) {
    const { t } = useTranslation();
    const [paste, setPaste] = useState("");
    const [busy, setBusy] = useState(false);

    // Extract een zvr_mcp_… token uit wat de gebruiker ook plakt: raw token,
    // MCP-config JSON of een URL met Authorization-header.
    const saveMcp = async () => {
        const raw = paste.trim();
        if (!raw) return;
        if (/[*…]{2,}|\*\*\*/.test(raw)) {
            toast.error(t("integr.zevari_mcp_masked"));
            return;
        }
        const m = raw.match(/zvr_mcp_[A-Za-z0-9_-]{8,}/);
        const token = m ? m[0] : (!/\s/.test(raw) && raw.length >= 16 ? raw : null);
        if (!token) {
            toast.error(t("integr.zevari_mcp_notfound"));
            return;
        }
        setBusy(true);
        try {
            await api.patch("/integrations/zevari", { api_key: token });
            toast.success(t("integr.zevari_mcp_saved"));
            setPaste("");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-[#38bdf8]/20 bg-[#38bdf8]/[0.04] p-4" data-testid="zevari-hint">
                <div className="flex items-start gap-2.5">
                    <ShieldCheck size={16} className="text-[#38bdf8] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#38bdf8]">{t("integr.zevari_hint_title")}</div>
                        <p className="text-[11px] text-[var(--jr-text-2)] mt-1 leading-relaxed">{t("integr.zevari_hint")}</p>
                        <a href="https://zevari.ai/dashboard/settings" target="_blank" rel="noreferrer"
                            data-testid="zevari-dashboard-link"
                            className="inline-flex items-center gap-1.5 mt-2 text-[11px] text-[#38bdf8] hover:text-white">
                            <ExternalLink size={11} /> {t("integr.zevari_open_dashboard")}
                        </a>
                    </div>
                </div>
            </div>
            {/* MCP-config / token plakken — extraheert automatisch het zvr_mcp_… token */}
            <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)]/40 p-4" data-testid="zevari-mcp-panel">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                    <Plug size={11} /> {t("integr.zevari_mcp_title")}
                </div>
                <p className="text-[11px] text-[var(--jr-text-3)] mt-1">{t("integr.zevari_mcp_sub")}</p>
                <textarea value={paste} onChange={(e) => setPaste(e.target.value)}
                    rows={2}
                    placeholder={'{"mcpServers": {"zevari": {"url": "https://mcp.zevari.ai/mcp", "headers": {"Authorization": "Bearer zvr_mcp_…"}}}}'}
                    data-testid="zevari-mcp-input"
                    className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono outline-none focus:border-[#38bdf8] resize-y" />
                <div className="mt-2 flex items-center gap-3">
                    <button onClick={saveMcp} disabled={busy || !paste.trim()} data-testid="zevari-mcp-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#38bdf8] text-black text-xs font-medium hover:bg-[#38bdf8]/85 disabled:opacity-50">
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                        {t("integr.zevari_mcp_savebtn")}
                    </button>
                    {cfg.configured && cfg.api_key_masked && (
                        <span className="text-[10px] font-mono text-[var(--jr-text-3)]" data-testid="zevari-mcp-current">
                            {t("integr.zevari_mcp_current")}: {cfg.api_key_masked}
                        </span>
                    )}
                </div>
            </div>
            <GenericVault provider={provider} cfg={cfg} onSaved={onSaved} appBaseUrl="" />
        </div>
    );
}

const ZEVARI_ACTIONS = [
    { id: "context",   labelKey: "integr.zevari_act_context",   icon: Plug },
    { id: "safety",    labelKey: "integr.zevari_act_safety",    icon: ShieldCheck },
    { id: "campaigns", labelKey: "integr.zevari_act_campaigns", icon: Send },
    { id: "leads",     labelKey: "integr.zevari_act_leads",     icon: Database },
];

function zevariStats(action, data, t) {
    if (!data) return [];
    if (action === "context") {
        const c = data.current_context || {};
        return [
            [t("integr.zevari_lbl_org"), c.organization_name],
            [t("integr.zevari_lbl_sender"), c.linkedin_account_label],
            [t("integr.zevari_lbl_email"), c.user_email],
        ];
    }
    if (action === "safety") {
        const weekly = data.weeklyUsage?.connection_request || {};
        return [
            [t("integr.zevari_lbl_status"), data.currentStatus],
            [t("integr.zevari_lbl_tier"), data.linkedInTier],
            [t("integr.zevari_lbl_invites_week"), `${weekly.used ?? 0}/${weekly.limit ?? "?"}`],
        ];
    }
    if (action === "campaigns") {
        const camps = data.campaigns || [];
        if (!camps.length) return [[t("integr.zevari_act_campaigns"), t("integr.zevari_no_campaigns")]];
        return camps.slice(0, 6).map((c) => [c.name || c.id, c.status || c.campaign_type || ""]);
    }
    if (action === "leads") {
        const l = data.leads || {}, c = data.campaigns || {}, li = data.lists || {};
        return [
            [t("integr.zevari_lbl_total_leads"), l.total_leads],
            [t("integr.zevari_lbl_active_7d"), l.active_last_7d],
            [t("integr.zevari_lbl_qualified"), l.qualified],
            [t("integr.zevari_lbl_active_campaigns"), c.active_campaigns],
            [t("integr.zevari_lbl_lists"), li.total_lists],
        ];
    }
    return [];
}

export function ZevariProbePanel() {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(null);       // action id currently running
    const [result, setResult] = useState(null);    // {action, data, text, latency_ms}
    const [error, setError] = useState(null);

    const run = async (action) => {
        setBusy(action); setError(null);
        try {
            const r = await api.post("/integrations/zevari/probe", { action });
            setResult(r.data);
            toast.success(t("integr.zevari_probe_done", { ms: r.data.latency_ms }));
        } catch (e) {
            setResult(null);
            setError(e?.response?.data?.detail || t("integr.probe_failed"));
        } finally { setBusy(null); }
    };

    const stats = result ? zevariStats(result.action, result.data, t) : [];
    const paused = result?.action === "safety" && result?.data?.safetyPaused;

    return (
        <div className="mt-3 rounded-xl border border-[#38bdf8]/20 bg-[#38bdf8]/[0.03] p-4" data-testid="zevari-probe-panel">
            <div className="flex items-start gap-2.5">
                <FlaskConical size={16} className="text-[#38bdf8] mt-0.5 shrink-0" />
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#38bdf8]">{t("integr.zevari_probe_title")}</div>
                    <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5 max-w-md">{t("integr.zevari_probe_sub")}</p>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
                {ZEVARI_ACTIONS.map((a) => {
                    const AIcon = a.icon;
                    const running = busy === a.id;
                    return (
                        <button key={a.id} onClick={() => run(a.id)} disabled={!!busy}
                            data-testid={`zevari-probe-${a.id}`}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                                result?.action === a.id
                                    ? "bg-[#38bdf8] text-black border-[#38bdf8]"
                                    : "bg-[#38bdf8]/10 text-[#38bdf8] border-[#38bdf8]/30 hover:bg-[#38bdf8]/20"
                            }`}>
                            {running ? <Loader2 size={12} className="animate-spin" /> : <AIcon size={12} />}
                            {t(a.labelKey)}
                        </button>
                    );
                })}
            </div>

            {error && (
                <div className="mt-3 rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/[0.08] p-3 text-xs inline-flex items-start gap-2"
                    data-testid="zevari-probe-error">
                    <AlertTriangle size={14} className="text-[#ef4444] mt-0.5 shrink-0" />
                    <span className="text-[var(--jr-text-2)]">{error}</span>
                </div>
            )}

            {result && (
                <div className="mt-3 space-y-2" data-testid="zevari-probe-result">
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">
                        <CheckCircle2 size={12} className="text-[#22c55e]" />
                        <span className="text-[#22c55e]">{result.tool}</span>
                        <span>· {result.latency_ms}ms</span>
                    </div>
                    {paused && (
                        <div className="rounded-lg border border-[var(--jr-amber)]/30 bg-[var(--jr-amber)]/[0.08] px-3 py-2 text-xs text-[var(--jr-amber)] inline-flex items-center gap-2">
                            <AlertTriangle size={13} /> {t("integr.zevari_paused_warn")}
                        </div>
                    )}
                    {stats.length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {stats.map(([label, value], i) => (
                                <div key={i} className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 px-3 py-2"
                                    data-testid={`zevari-stat-${result.action}-${i}`}>
                                    <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
                                    <div className="text-xs text-white mt-0.5 break-words">{value ?? "—"}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <pre className="rounded-lg border border-white/8 bg-black/30 p-3 text-[10px] text-[var(--jr-text-2)] overflow-x-auto max-h-48">
                            {result.text || JSON.stringify(result.data, null, 2)}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
}

export default ZevariVault;
